import AsyncStorage from '@/platform/async-storage';
import { FaceArcFace } from './FaceArcFace';
import * as FileSystem from '@/platform/file-system';
import * as ImageManipulator from '@/platform/image-manipulator';
import type { TensorflowModel } from '@/ml/tflite';

import { TirvuFace, type DetectedFace, type Tile } from '@/ml/tirvu-face';

/**
 * Reconhecimento facial 1:1 — "esta pessoa é ela mesma?".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * O QUE MUDOU (e por quê o de antes não funcionava)
 * ─────────────────────────────────────────────────────────────────────────────
 * O recorte do rosto saiu daqui e foi para o módulo nativo `tirvu-face`, que faz o
 * **alinhamento ArcFace** — a transformação de similaridade que o mobilefacenet
 * espera. O que existia antes (`crop(caixa + 25%) → resize(112x112)`) não removia a
 * inclinação da cabeça, ESTICAVA o rosto ao encaixar uma caixa retangular num
 * quadrado, e usava enquadramento diferente do treino. Ver o cabeçalho de
 * `modules/tirvu-face/index.ts` para a explicação completa.
 *
 * A inferência continua aqui, em JS, no `react-native-fast-tflite` — de propósito.
 * Levar o TFLite para o nativo obrigaria a conviver com os binários que o fast-tflite
 * já embarca, que é a classe de conflito de pod/.so que já queimou este projeto duas
 * vezes (ver `react-native.config.js`).
 */

const MODEL_INPUT_SIZE = 112;
const REFERENCE_STORAGE_KEY = '@TirvuMais:face_reference';

/**
 * ── Dois motores, um de cada vez ────────────────────────────────────────────
 *
 * **ArcFace** (`w600k_mbf`, 512-d) é o preferido, e não por acurácia: é o MESMO
 * espaço de embedding do coletor TirvuTab e do tab web. Com os três iguais, a
 * biometria cadastrada aqui serve lá — o colaborador cadastra uma vez, no
 * celular, e bate ponto no tablet do posto sem recadastrar.
 *
 * **MobileFaceNet** (192-d) fica como reserva, para o ambiente onde o módulo
 * nativo do ONNX Runtime não existe (Expo Go, build sem o pacote). Melhor
 * degradar para um motor que só funciona neste aparelho do que não funcionar.
 *
 * ⚠️ Os dois NUNCA se misturam. Um embedding de 192-d e um de 512-d não são
 * comparáveis, e comparar daria similaridade baixa para a MESMA pessoa — ou
 * seja, travaria o ponto de todo mundo em silêncio. Quem separa é a versão
 * gravada junto do template.
 */
type Motor = 'arcface' | 'mobilefacenet';

/** Resolvido uma vez, na primeira extração. */
let motorAtivo: Motor | null = null;

const resolverMotor = async (): Promise<Motor> => {
  if (motorAtivo) return motorAtivo;
  motorAtivo = (await FaceArcFace.arcFaceDisponivel()) ? 'arcface' : 'mobilefacenet';
  console.log(`[FaceRecognition] Motor: ${motorAtivo}`);
  return motorAtivo;
};

const DIMENSAO: Record<Motor, number> = { arcface: 512, mobilefacenet: 192 };


/**
 * A migração roda no máximo UMA vez por execução do app.
 *
 * `hasReference` é chamada por várias telas e em cada foco; migrar em toda
 * chamada faria uma inferência inteira por vez — cara, e inútil da segunda em
 * diante, porque o resultado não muda dentro da mesma sessão.
 */
let promessaMigracao: Promise<unknown> | null = null;
const migrarUmaVez = (): Promise<unknown> => {
  if (!promessaMigracao) {
    promessaMigracao = FaceRecognition.migrarSePreciso().catch(() => 'recadastrar');
  }
  return promessaMigracao;
};

/**
 * Versão do template biométrico gravado.
 *
 * **v1** = pipeline SEM alinhamento (histórico, nunca mais gerado).
 * **v2** = MobileFaceNet 192-d, alinhado.
 * **v3** = ArcFace 512-d — o espaço compartilhado com o TirvuTab e o tab web.
 *
 * Versão diferente da atual NÃO é convertida: não existe conversão entre espaços
 * de embedding, e comparar dois deles daria similaridade baixa para a MESMA
 * pessoa — travaria o ponto de todo mundo sem erro nenhum para avisar.
 *
 * Mas também não é simplesmente descartada. Antes de mandar recadastrar, o app
 * tenta REPROCESSAR a foto do cadastro, que fica em `documentDirectory`
 * (armazenamento durável, não cache) — ver `persistirFotoCadastro`. Só quando a
 * foto sumiu, ou não tem mais rosto utilizável, é que o colaborador precisa
 * refazer.
 */
const VERSAO: Record<Motor, number> = { arcface: 3, mobilefacenet: 2 };


/**
 * Limiar de decisão sobre o cosseno entre embeddings L2-normalizados.
 *
 * ⚠️ CALIBRE EM CAMPO antes de considerar fechado. Com o alinhamento correto os pares
 * genuínos sobem bastante (o pipeline antigo os empurrava para 0,55–0,65, colados no
 * limiar de 0,6 — era essa sobreposição que fazia ora rejeitar o dono, ora aceitar
 * outra pessoa). O procedimento está em `assets/models/README.md`.
 *
 * A escolha aqui é deliberadamente conservadora: num relógio de ponto, aceitar quem
 * não é (falso positivo) é fraude registrada; rejeitar quem é (falso negativo) é só
 * pedir outra foto. Na dúvida, SUBA o limiar.
 */
const SIMILARITY_THRESHOLD = 0.65;
/** Cadastro: similaridade MÍNIMA entre os frames aceitos (mesmo rosto, parado). */
const CONSISTENCIA_MIN_CADASTRO = 0.45;

/**
 * Zona cinza: acima disso mas abaixo do limiar, provavelmente é a pessoa certa com
 * uma foto ruim. O chamador pode usar para pedir nova tentativa em vez de acusar
 * fraude — a mensagem muda, a decisão (bloquear) não.
 */
const GRAY_ZONE = 0.55;

/** Portões de qualidade. Ver `avaliarQualidade`. */
const QUALIDADE = {
  /**
   * Distância entre os olhos, em pixels. Abaixo disso o rosto simplesmente não tem
   * resolução para um embedding estável — o tile 112x112 vira upscale de borrão.
   */
  INTEROCULAR_MIN: 45,
  /** Graus. O alinhamento corrige o roll, mas roll extremo indica landmark ruim. */
  ROLL_MAX: 35,
  /** Graus. Rosto virado esconde metade dos traços — nem o alinhamento salva. */
  YAW_MAX: 22,
  PITCH_MAX: 22,
  /** Variância do laplaciano no tile alinhado (escala 0–255²). Baixo = borrado. */
  NITIDEZ_MIN: 55,
  /** Luminância média do tile alinhado. Fora disso, ou estourou ou está no escuro. */
  BRILHO_MIN: 40,
  BRILHO_MAX: 235,
};

/**
 * Distância interocular mínima, em pixels, para o CADASTRO aceitar a foto.
 *
 * Exportado de propósito: a tela de captura precisa orientar a pessoa pelo MESMO
 * número que vai reprová-la. Guiar por um critério e reprovar por outro é como se
 * acumulam frames que nunca seriam aceitos — a pessoa segue a instrução na tela e
 * ainda assim leva 'não'.
 *
 * ⚠️ Tem que ficar DEPOIS de `QUALIDADE`. Acima dela, isto é zona morta temporal:
 * `const` não sofre hoisting de valor, então o módulo estouraria `ReferenceError`
 * já no import — e derrubaria toda tela que importa daqui.
 */
export const INTEROCULAR_MIN_CADASTRO = QUALIDADE.INTEROCULAR_MIN;

/**
 * As portas de ÂNGULO do cadastro, pelo mesmo motivo do interocular acima.
 *
 * ⚠️ O `pitch` é o que mais pega em campo e era o único sem aviso ao vivo. Aparelho na
 * mão, na altura do peito, com a pessoa olhando para baixo: passa de 22° com folga. A
 * tela então acumulava os 3 frames, a barra chegava a 100%, e só depois de "Processando
 * biometria..." vinha "Mantenha o rosto na altura da câmera" — sem a pessoa ter como
 * adivinhar que precisava LEVANTAR o aparelho.
 *
 * ⚠️ E `YAW_MAX` é 22, não 28: a tela reprovava a 28 e o cadastro a 22, então os frames
 * entre os dois números eram guardados só para morrer depois. Guiar por um critério e
 * reprovar por outro é o defeito, não o número em si.
 */
export const YAW_MAX_CADASTRO = QUALIDADE.YAW_MAX;
export const PITCH_MAX_CADASTRO = QUALIDADE.PITCH_MAX;
export const ROLL_MAX_CADASTRO = QUALIDADE.ROLL_MAX;

/** Quantos templates guardamos por colaborador (cadastro multi-foto). */
const MAX_TEMPLATES = 5;

// O antigo `FaceBounds` saiu daqui: os recortes agora são responsabilidade do
// `tirvu-face`, que trabalha com `Box` + landmarks (ver modules/tirvu-face/index.ts).
// Passar caixa solta entre módulos era justamente o que permitia recortar a imagem
// errada com as coordenadas de outra.

type StoredReference = {
  v: number;
  embeddings: number[][];
  updatedAt: number;
  sourceHash?: string;
  /** Nome do arquivo (não a URI absoluta) da foto do cadastro. Ver `getReferenceImageUri`. */
  imageUri?: string;
};

export type Qualidade = {
  ok: boolean;
  motivo: string | null;
  interocularPx: number;
  roll: number;
  yaw: number;
  pitch: number;
  nitidez: number;
  brilho: number;
};

export type ResultadoVerificacao = {
  matched: boolean;
  similarity: number;
  hasReference: boolean;
  /** true quando ficou na zona cinza — provável foto ruim, não impostor. */
  duvidoso: boolean;
  qualidade: Qualidade | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Modelo
// ─────────────────────────────────────────────────────────────────────────────

let modelPromise: Promise<TensorflowModel | null> | null = null;

const loadModel = (): Promise<TensorflowModel | null> => {
  if (!modelPromise) {
    modelPromise = (async () => {
      try {
        const { loadTensorflowModel } = await import('@/ml/tflite');
        return await loadTensorflowModel("/models/mobilefacenet.tflite" /* WEB: modelo servido pelo PWA */);
      } catch {
        return null;
      }
    })();
  }
  return modelPromise;
};

/** Spec do tile que o mobilefacenet consome. Exportado: o orquestrador pede junto com os outros. */
export const SPEC_RECONHECIMENTO = {
  size: MODEL_INPUT_SIZE,
  mode: 'arcface' as const,
};

/**
 * RGB [0,255] → float32 [-1,1], que é a normalização com que o mobilefacenet foi
 * treinado ((pixel - 127.5) / 127.5).
 */
const buildModelInput = (rgb: Uint8Array): Float32Array => {
  const total = MODEL_INPUT_SIZE * MODEL_INPUT_SIZE * 3;
  if (rgb.length !== total) {
    throw new Error(`Tile de reconhecimento com ${rgb.length} bytes, esperado ${total}`);
  }
  const input = new Float32Array(total);
  for (let i = 0; i < total; i++) {
    input[i] = (rgb[i] - 127.5) / 127.5;
  }
  return input;
};

const l2Normalize = (vector: Float32Array | number[]): Float32Array => {
  let sum = 0;
  for (let i = 0; i < vector.length; i++) sum += vector[i] * vector[i];
  const norm = Math.sqrt(sum) || 1;
  const out = new Float32Array(vector.length);
  for (let i = 0; i < vector.length; i++) out[i] = vector[i] / norm;
  return out;
};

const cosineSimilarity = (a: Float32Array | number[], b: Float32Array | number[]): number => {
  if (a.length !== b.length) return 0;
  let dot = 0;
  for (let i = 0; i < a.length; i++) dot += a[i] * b[i];
  return dot;
};

// ─────────────────────────────────────────────────────────────────────────────
// Qualidade — medida SOBRE O TILE JÁ ALINHADO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Nitidez pela variância do laplaciano, e luminância média. Rodam sobre o tile
 * 112x112 (12.544 pixels) — barato o suficiente para não valer ida ao nativo, e fica
 * em JS onde é fácil de ajustar sem rebuild.
 */
const medirTile = (rgb: Uint8Array, size: number): { nitidez: number; brilho: number } => {
  const total = size * size;
  const cinza = new Float32Array(total);
  let soma = 0;
  for (let i = 0; i < total; i++) {
    const v = 0.299 * rgb[i * 3] + 0.587 * rgb[i * 3 + 1] + 0.114 * rgb[i * 3 + 2];
    cinza[i] = v;
    soma += v;
  }

  // Laplaciano 4-vizinhos, ignorando a borda.
  let somaLap = 0;
  let somaLap2 = 0;
  let n = 0;
  for (let y = 1; y < size - 1; y++) {
    for (let x = 1; x < size - 1; x++) {
      const i = y * size + x;
      const lap =
        cinza[i - 1] + cinza[i + 1] + cinza[i - size] + cinza[i + size] - 4 * cinza[i];
      somaLap += lap;
      somaLap2 += lap * lap;
      n++;
    }
  }

  const media = n ? somaLap / n : 0;
  return {
    nitidez: n ? somaLap2 / n - media * media : 0,
    brilho: total ? soma / total : 0,
  };
};

/**
 * O detector ACHOU um rosto — mas é um rosto?
 *
 * O ML Kit (e o Vision) às vezes "vê" um rosto em textura de teto, luminária ou
 * estampa, e devolve landmarks e ângulos para ele com a mesma cara de um rosto de
 * verdade. Nada depois disto julga: o ArcFace gera um embedding de QUALQUER recorte,
 * e a prova de vida está desligada. Foi assim que um cadastro apontado para o TETO
 * foi aceito e gravado (12/09/2026).
 *
 * Esta porta confere se os pontos têm as PROPORÇÕES de um rosto: olhos na ordem,
 * boca abaixo dos olhos a uma distância compatível, largura da boca compatível com a
 * distância entre os olhos, nariz (quando há) entre os dois, e a caixa com aspecto de
 * rosto. Tolerâncias largas de propósito — o objetivo é barrar o absurdo, não julgar
 * pose (disso cuidam yaw/pitch/roll). Devolve o motivo, ou null quando é plausível.
 */
const implausibilidadeGeometrica = (face: DetectedFace): string | null => {
  const lm = face.landmarks;
  if (!lm) return 'Não identificamos um rosto válido na foto';
  const io = face.interocularPx;
  if (!(io > 0)) return 'Não identificamos um rosto válido na foto';
  const { leftEye, rightEye, mouthLeft, mouthRight, noseBase } = lm;
  const eixoOlhos = (leftEye.y + rightEye.y) / 2;
  const bocaY = (mouthLeft.y + mouthRight.y) / 2;
  const bocaLargura = Math.hypot(mouthRight.x - mouthLeft.x, mouthRight.y - mouthLeft.y);
  const olhosDx = rightEye.x - leftEye.x;

  const ok =
    olhosDx > 0.6 * io && // olhos na ordem e quase horizontais (o roll já é limitado)
    bocaY - eixoOlhos > 0.55 * io && // boca bem abaixo dos olhos…
    bocaY - eixoOlhos < 2.2 * io && // …mas não a um rosto de distância
    bocaLargura > 0.45 * io &&
    bocaLargura < 1.8 * io &&
    (mouthLeft.x + mouthRight.x) / 2 > leftEye.x - 0.6 * io && // boca sob os olhos
    (mouthLeft.x + mouthRight.x) / 2 < rightEye.x + 0.6 * io &&
    (!noseBase ||
      (noseBase.y > eixoOlhos + 0.2 * io &&
        noseBase.y < bocaY &&
        noseBase.x > leftEye.x - 0.5 * io &&
        noseBase.x < rightEye.x + 0.5 * io)) &&
    face.box.width > 0 &&
    face.box.height > 0 &&
    face.box.height / face.box.width > 0.55 &&
    face.box.height / face.box.width < 1.9 &&
    io / face.box.width > 0.2 &&
    io / face.box.width < 0.75;

  return ok ? null : 'Não identificamos um rosto válido na foto';
};

const avaliarQualidade = (
  face: DetectedFace,
  medidas: { nitidez: number; brilho: number },
  estrito: boolean,
): Qualidade => {
  const base: Qualidade = {
    ok: true,
    motivo: null,
    interocularPx: face.interocularPx,
    roll: face.roll,
    yaw: face.yaw,
    pitch: face.pitch,
    nitidez: medidas.nitidez,
    brilho: medidas.brilho,
  };

  const reprova = (motivo: string): Qualidade => ({ ...base, ok: false, motivo });

  if (face.interocularPx < QUALIDADE.INTEROCULAR_MIN) {
    return reprova('Aproxime o rosto da câmera');
  }
  if (Math.abs(face.yaw) > QUALIDADE.YAW_MAX) {
    return reprova('Olhe diretamente para a câmera');
  }
  if (Math.abs(face.pitch) > QUALIDADE.PITCH_MAX) {
    return reprova('Mantenha o rosto na altura da câmera');
  }
  if (Math.abs(face.roll) > QUALIDADE.ROLL_MAX) {
    return reprova('Endireite a cabeça');
  }

  // Nitidez e iluminação só REPROVAM no cadastro. Uma referência ruim é permanente e
  // envenena toda verificação futura; uma verificação ruim custa só outra foto. Por
  // isso o portão é estrito na hora de gravar e frouxo na hora de conferir.
  if (estrito) {
    if (medidas.nitidez < QUALIDADE.NITIDEZ_MIN) {
      return reprova('Foto sem nitidez — segure o aparelho firme');
    }
    if (medidas.brilho < QUALIDADE.BRILHO_MIN) {
      return reprova('Ambiente muito escuro');
    }
    if (medidas.brilho > QUALIDADE.BRILHO_MAX) {
      return reprova('Luz forte demais — evite contraluz');
    }
  }

  return base;
};

// ─────────────────────────────────────────────────────────────────────────────
// Detecção + extração
// ─────────────────────────────────────────────────────────────────────────────

/** Detecta e devolve o MAIOR rosto da foto, ou null. */
const maiorRosto = async (imageUri: string): Promise<DetectedFace | null> => {
  const res = await TirvuFace.detect(imageUri);
  // O nativo já devolve ordenado por área — o primeiro é o maior.
  const face = res.faces[0];
  if (!face || !face.landmarks) return null;
  return face;
};

const tileReconhecimento = async (imageUri: string, face: DetectedFace): Promise<Tile> => {
  const [tile] = await TirvuFace.extractTiles(imageUri, face, [SPEC_RECONHECIMENTO]);
  return tile;
};

const hashBase64 = (base64: string): string => {
  let hash = 5381;
  for (let i = 0; i < base64.length; i++) {
    hash = ((hash << 5) + hash + base64.charCodeAt(i)) | 0;
  }
  return `${base64.length}_${(hash >>> 0).toString(36)}`;
};

/** O que está no disco, sem julgar versão. Só quem migra precisa disto. */
const lerArmazenadoBruto = async (): Promise<StoredReference | null> => {
  const raw = await AsyncStorage.getItem(REFERENCE_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredReference;
    return Array.isArray(parsed?.embeddings) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Templates utilizáveis PELO MOTOR ATIVO.
 *
 * Versão diferente = outro espaço de embedding. Não converte, não compara: ou o
 * `migrarSePreciso` refaz a partir da foto guardada, ou o colaborador recadastra.
 * Comparar espaços diferentes daria similaridade baixa para a mesma pessoa e
 * travaria o ponto de todo mundo, sem nenhum erro para avisar.
 */
const lerArmazenado = async (): Promise<StoredReference | null> => {
  const motor = await resolverMotor();
  const parsed = await lerArmazenadoBruto();
  if (!parsed) return null;
  if (parsed.v !== VERSAO[motor]) return null;
  if (parsed.embeddings.length === 0) return null;
  if (parsed.embeddings.some((e) => !Array.isArray(e) || e.length !== DIMENSAO[motor])) {
    return null;
  }
  return parsed;
};

export const FaceRecognition = {
  /** Porta geométrica de "isto é um rosto?" — a tela de cadastro usa no laço. */
  implausibilidadeGeometrica,
  /** Pré-carrega o modelo em memória (use no login pra evitar latência no ponto). */
  warmup: async (): Promise<void> => {
    // Aquece o motor ATIVO, não os dois.
    //
    // ⚠️ Isto carregava o mobilefacenet SEMPRE — inclusive quando o ArcFace é quem
    // vai rodar. São 5 MB de modelo reserva ocupando memória nativa e tempo de
    // carga no aparelho que menos tem dos dois, para nunca ser usado.
    //
    // `resolverMotor` já aquece o ArcFace de graça: ele decide chamando
    // `arcFaceDisponivel()`, que cria a sessão do ONNX Runtime para responder.
    if ((await resolverMotor()) === 'arcface') return;
    await loadModel();
  },

  /** false em Expo Go / build sem o nativo. O chamador deve degradar, não quebrar. */
  disponivel: (): boolean => TirvuFace.isAvailable(),

  /** Gera o embedding a partir de um tile 112x112 JÁ ALINHADO pelo `tirvu-face`. */
  embedFromTile: async (tile: Tile): Promise<Float32Array> => {
    // ArcFace primeiro: é o espaço compartilhado com o TirvuTab e o tab web.
    // O tile já vem alinhado do `tirvu-face`; o que muda entre os motores é o
    // LAYOUT (NCHW aqui, NHWC no mobilefacenet), não a normalização.
    if ((await resolverMotor()) === 'arcface') {
      const v = await FaceArcFace.descritorDeTile(tile.data, MODEL_INPUT_SIZE);
      if (v) return v;
      throw new Error('ArcFace indisponível no momento da extração');
    }

    const model = await loadModel();
    if (!model) throw new Error('Módulo TFLite indisponível (requer dev build)');

    const input = buildModelInput(tile.data);

    // Defesa: o fast-tflite só valida shape/dtype em build DEBUG; em release um
    // mismatch de buffer pode estourar nativamente (não capturável). Validamos no
    // JS p/ degradar com Error capturável em vez de fechar o app.
    const inT: any = (model as any).inputs?.[0];
    if (inT?.dataType && inT.dataType !== 'float32') {
      throw new Error(`mobilefacenet.tflite dtype ${inT.dataType} (esperado float32)`);
    }
    const inElems = Array.isArray(inT?.shape)
      ? inT.shape.reduce((a: number, n: number) => a * (n || 1), 1)
      : 0;
    if (inElems && inElems !== input.length) {
      throw new Error(`mobilefacenet.tflite espera ${inElems} elems, recebeu ${input.length}`);
    }

    const outputs = await model.run([input]);
    const raw = outputs[0] as Float32Array;

    // Aqui só chega o caminho de reserva, então a dimensão é a dele.
    if (!raw || raw.length !== DIMENSAO.mobilefacenet) {
      throw new Error(
        `Embedding inesperado (esperado ${DIMENSAO.mobilefacenet}, veio ${raw?.length})`,
      );
    }

    return l2Normalize(raw);
  },

  /**
   * Detecta, alinha e gera o embedding de uma foto, com portão de qualidade.
   * `estrito` = true no cadastro (ver `avaliarQualidade`).
   */
  analisar: async (
    imageUri: string,
    opts?: { face?: DetectedFace; estrito?: boolean },
  ): Promise<{ embedding: Float32Array; face: DetectedFace; qualidade: Qualidade }> => {
    const face = opts?.face ?? (await maiorRosto(imageUri));
    if (!face) throw new Error('Nenhum rosto identificado na foto');
    // No CADASTRO, um rosto fantasma do detector vira referência permanente. Barra
    // aqui o que não tem proporção de rosto (ver `implausibilidadeGeometrica`).
    if (opts?.estrito) {
      const motivo = implausibilidadeGeometrica(face);
      if (motivo) throw new Error(motivo);
    }

    const tile = await tileReconhecimento(imageUri, face);
    const medidas = medirTile(tile.data, tile.size);
    const qualidade = avaliarQualidade(face, medidas, opts?.estrito ?? false);
    if (!qualidade.ok) throw new Error(qualidade.motivo ?? 'Foto sem qualidade suficiente');

    const embedding = await FaceRecognition.embedFromTile(tile);
    return { embedding, face, qualidade };
  },

  /** Compara dois embeddings já normalizados. Retorna similaridade [-1,1]. */
  compare: (a: Float32Array | number[], b: Float32Array | number[]): number =>
    cosineSimilarity(a, b),

  isMatch: (similarity: number): boolean => similarity >= SIMILARITY_THRESHOLD,

  /**
   * Grava os templates de referência. Aceita 1..MAX_TEMPLATES embeddings — cadastrar
   * com várias fotos (ângulos/luz diferentes) é o que mais melhora a taxa de acerto
   * depois do alinhamento, porque a comparação passa a ser contra o MELHOR template.
   */
  saveReference: async (
    embeddings: Float32Array[],
    sourceHash?: string,
    imageUri?: string,
  ): Promise<void> => {
    const payload: StoredReference = {
      v: VERSAO[await resolverMotor()],
      embeddings: embeddings.slice(0, MAX_TEMPLATES).map((e) => Array.from(e)),
      updatedAt: Date.now(),
      sourceHash,
      imageUri,
    };
    await AsyncStorage.setItem(REFERENCE_STORAGE_KEY, JSON.stringify(payload));
  },

  /** Templates salvos, ou null se não houver cadastro VÁLIDO NA VERSÃO ATUAL. */
  loadReference: async (): Promise<Float32Array[] | null> => {
    const stored = await lerArmazenado();
    if (!stored) return null;
    return stored.embeddings.map((e) => Float32Array.from(e));
  },

  loadReferenceHash: async (): Promise<string | null> => {
    const stored = await lerArmazenado();
    return stored?.sourceHash ?? null;
  },

  /**
   * Existe biometria utilizável PELO MOTOR ATIVO?
   *
   * ⚠️ Migra ANTES de responder. Quando o motor muda (MobileFaceNet 192-d →
   * ArcFace 512-d), o template guardado deixa de valer — espaços de embedding
   * diferentes não se comparam. Sem a migração aqui, esta função responderia
   * `false` para quem JÁ tem cadastro e as telas empurrariam a pessoa para
   * recadastrar sem necessidade.
   *
   * A migração vive AQUI, e não em cada tela, de propósito: `Registro`,
   * `Camera` e `CameraDiaria` chamam esta função para decidir se bloqueiam o
   * ponto. Deixar a migração a cargo de quem chama significaria que basta
   * alguém esquecer numa tela nova para o colaborador ser mandado recadastrar
   * por um caminho e não por outro.
   */
  hasReference: async (): Promise<boolean> => {
    await migrarUmaVez();
    const ref = await lerArmazenado();
    return ref != null;
  },

  clearReference: async (): Promise<void> => {
    // Remove também a foto persistida do cadastro. Lê o RAW (não `lerArmazenado`):
    // uma referência v1 precisa ter a foto apagada igual, e `lerArmazenado` a descarta.
    try {
      const raw = await AsyncStorage.getItem(REFERENCE_STORAGE_KEY);
      const nome = raw ? (JSON.parse(raw) as StoredReference).imageUri : null;
      if (nome) {
        const dir = (FileSystem as any).documentDirectory || '';
        const base = String(nome).split('/').pop();
        if (base) {
          try {
            await FileSystem.deleteAsync(`${dir}${base}`, { idempotent: true });
          } catch {
            /* ignore */
          }
        }
      }
    } catch {
      /* ignore */
    }
    await AsyncStorage.removeItem(REFERENCE_STORAGE_KEY);
  },

  /**
   * Persiste a foto do cadastro em documentDirectory, com nome único (evita cache do
   * <Image> ao re-cadastrar) e removendo a anterior. Devolve só o NOME do arquivo.
   */
  persistReferenceImage: async (srcUri: string): Promise<string | null> => {
    try {
      if (!srcUri) return null;
      let oldName: string | null = null;
      try {
        const raw = await AsyncStorage.getItem(REFERENCE_STORAGE_KEY);
        if (raw) oldName = (JSON.parse(raw) as StoredReference).imageUri ?? null;
      } catch {
        /* ignore */
      }

      const dir = (FileSystem as any).documentDirectory;
      if (!dir) return null;
      const fileName = `biometria_ref_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
      const destUri = `${dir}${fileName}`;

      // Persiste via ImageManipulator (lê file://, content://, ph:// de forma confiável),
      // gerando um JPEG pequeno em cache; depois copia para documentDirectory. Evita a
      // falha silenciosa do copyAsync direto sobre a URI bruta da câmera.
      let persisted = false;
      try {
        const manip = await ImageManipulator.manipulateAsync(
          srcUri,
          [{ resize: { width: 512 } }],
          { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG },
        );
        if (manip?.uri) {
          await FileSystem.copyAsync({ from: manip.uri, to: destUri });
          persisted = true;
          try {
            await FileSystem.deleteAsync(manip.uri, { idempotent: true });
          } catch {
            /* ignore */
          }
        }
      } catch (e) {
        console.warn('[BIO] persist via manipulator falhou:', e);
      }

      if (!persisted) {
        await FileSystem.copyAsync({ from: srcUri, to: destUri });
      }

      const info = await FileSystem.getInfoAsync(destUri);
      if (!info.exists) return null;

      const oldBase = oldName ? oldName.split('/').pop() : null;
      if (oldBase && oldBase !== fileName) {
        try {
          await FileSystem.deleteAsync(`${dir}${oldBase}`, { idempotent: true });
        } catch {
          /* ignore */
        }
      }

      // Guarda só o NOME (relativo): o caminho absoluto do documentDirectory muda entre
      // execuções/updates (principalmente no iOS), o que faria a foto "sumir".
      return fileName;
    } catch {
      return null;
    }
  },

  /** URI da foto do cadastro facial salvo, ou null. */
  getReferenceImageUri: async (): Promise<string | null> => {
    try {
      const raw = await AsyncStorage.getItem(REFERENCE_STORAGE_KEY);
      if (!raw) return null;
      const stored = (JSON.parse(raw) as StoredReference).imageUri;
      if (!stored) return null;
      // Reconstrói a partir do documentDirectory ATUAL. Aceita o formato novo (só o
      // nome) e o antigo (URI absoluta), sempre usando apenas o nome do arquivo.
      const dir = (FileSystem as any).documentDirectory || '';
      const fileName = String(stored).split('/').pop() || String(stored);
      const uri = `${dir}${fileName}`;
      const info = await FileSystem.getInfoAsync(uri);
      return info.exists ? uri : null;
    } catch {
      return null;
    }
  },

  /**
   * Cadastra a referência a partir de uma ou mais fotos (URIs).
   *
   * Passar várias fotos é o caminho recomendado: cada uma vira um template e a
   * verificação compara contra o melhor deles. Fotos que reprovarem no portão de
   * qualidade são DESCARTADAS — se nenhuma passar, o cadastro falha em vez de gravar
   * uma referência ruim (que seria permanente e quebraria o ponto do colaborador).
   */
  /**
   * ── Migração entre espaços de embedding ────────────────────────────────
   *
   * Chamada no boot. Quando o template guardado é de OUTRO motor (trocou o
   * ArcFace pelo mobilefacenet ou vice-versa), ele deixa de valer — espaços
   * diferentes não se comparam.
   *
   * Em vez de mandar todo mundo recadastrar, tenta REPROCESSAR a foto do
   * cadastro. Ela fica em `documentDirectory` (durável, sobrevive a limpeza de
   * cache e a atualização do app), então na maioria dos casos a migração é
   * invisível: o colaborador abre o app e continua batendo ponto.
   *
   * ⚠️ `estrito: false` de propósito. A foto JÁ passou pelo portão estrito no
   * dia do cadastro; reaplicá-lo agora reprovaria referências boas por uma
   * margem qualquer e forçaria recadastro sem necessidade — exatamente o
   * atrito que esta função existe para evitar.
   *
   * ⚠️ A migração devolve UM template, não os vários do cadastro original: só a
   * melhor foto é persistida. Perde-se um pouco da robustez multi-ângulo. É o
   * preço de não obrigar a refazer, e o cadastro seguinte volta a ser multi-foto.
   *
   * `'recadastrar'` é o caminho honesto de última instância: sem foto guardada
   * (cadastro antigo, anterior à persistência) ou sem rosto utilizável nela, não
   * há o que reaproveitar. Melhor pedir uma foto nova AGORA do que o colaborador
   * descobrir na hora de bater o ponto.
   */
  /**
   * O cadastro local, no formato que o banco entende.
   *
   * Existe para a biometria deixar de morar só neste aparelho. Enviada ao banco,
   * ela passa a servir também ao coletor TirvuTab do posto — o colaborador
   * cadastra uma vez, no celular, e bate ponto no tablet sem recadastrar.
   *
   * ⚠️ O DESCRITOR só sai no espaço do ArcFace (`v10:`); a FOTO sai sempre.
   *
   * Um descritor do mobilefacenet (192-d) subiria como texto válido e seria
   * inútil no coletor: ele o descartaria por versão. Pior que inútil — enganaria
   * quem fosse depurar, porque o campo estaria preenchido.
   *
   * Mas a foto vale por si: o coletor tem um caminho de reprocessamento que
   * baixa a foto e gera o descritor no espaço DELE. Então mesmo no motor de
   * reserva a biometria chega lá — só demora uma inferência a mais, uma vez.
   * Não subir nada deixaria essa pessoa invisível no tablet, e ninguém saberia
   * por quê.
   *
   * A foto vai como **data URI**, que é o formato que o tab web grava
   * (`canvas.toDataURL`) e o que o painel do TirvuAdm espera para exibir. Base64
   * cru fica gravado num formato diferente e a miniatura aparece quebrada lá.
   */
  exportarParaBanco: async (): Promise<{ descritor: string; foto: string } | null> => {
    const motor = await resolverMotor();
    const guardado = await lerArmazenado();
    if (!guardado || guardado.embeddings.length === 0) return null;

    const nome = guardado.imageUri;
    const dir = (FileSystem as any).documentDirectory ?? '';
    if (!nome || !dir) return null;

    try {
      const base64 = await FileSystem.readAsStringAsync(`${dir}${nome}`, {
        encoding: 'base64' as any,
      });
      if (!base64) return null;

      return {
        // Vazio no motor de reserva: o backend guarda o campo como está e o
        // coletor, ao não conseguir desserializar, cai no reprocessamento da
        // foto — que é exatamente o comportamento desejado aqui.
        descritor:
          motor === 'arcface'
            ? FaceArcFace.serializarDescritor(Float32Array.from(guardado.embeddings[0]))
            : '',
        foto: `data:image/jpeg;base64,${base64}`,
      };
    } catch (err: any) {
      console.warn('[FaceRecognition] Falha ao exportar o cadastro:', err?.message ?? err);
      return null;
    }
  },

  /**
   * Registra localmente uma face que JÁ existe no servidor (12/09/2026) — a do próprio
   * colaborador, cadastrada no coletor de tablet, no TirvuAdm ou em outro celular. É o
   * que evita exigir um cadastro novo a cada login quando `permite_sem_biometria = 'N'`.
   *
   * Dois caminhos, nesta ordem:
   *   1. FOTO → embedding no espaço do MOTOR ATIVO (funciona no ArcFace e no reserva),
   *      não-estrita: a foto já foi aceita por algum cadastro; a foto fica guardada como
   *      imagem de referência (preview em Meus Dados e migração futura de motor);
   *   2. DESCRITOR (`v10:`) → só quando o motor ativo é o ArcFace, que é o espaço dele.
   *      Sem foto guardada — um dia, se o motor mudar, é recadastrar.
   * Nada disto sobe para o servidor: a face já está lá.
   */
  importarDoServidor: async (face: {
    descritor?: string | null;
    foto?: string | null;
  }): Promise<{ importado: boolean; via?: 'foto' | 'descritor'; reason?: string }> => {
    const motor = await resolverMotor();
    let motivo: string | undefined;

    // A foto pode vir como data URI (é como o tab web e o app gravam) ou base64 cru.
    const base64 = String(face.foto ?? '').replace(/^data:image\/[a-z0-9.+-]+;base64,/i, '').trim();
    if (base64.length >= 100) {
      const tempUri = `${(FileSystem as any).cacheDirectory}face_import_${Date.now()}.jpg`;
      try {
        await FileSystem.writeAsStringAsync(tempUri, base64, {
          encoding: FileSystem.EncodingType.Base64,
        });
        const { embedding } = await FaceRecognition.analisar(tempUri, { estrito: false });
        const nome = await FaceRecognition.persistReferenceImage(tempUri);
        await FaceRecognition.saveReference([embedding], hashBase64(base64), nome ?? undefined);
        console.log('[FaceRecognition] Biometria do servidor registrada localmente (pela foto).');
        return { importado: true, via: 'foto' };
      } catch (err: any) {
        motivo = err?.message ?? String(err);
        console.warn('[FaceRecognition] Foto do servidor não serviu como referência:', motivo);
      } finally {
        FileSystem.deleteAsync(tempUri, { idempotent: true }).catch(() => {});
      }
    }

    if (motor === 'arcface' && face.descritor) {
      const vetor = FaceArcFace.desserializarDescritor(face.descritor);
      if (vetor) {
        await FaceRecognition.saveReference([vetor], hashBase64(face.descritor));
        console.log('[FaceRecognition] Biometria do servidor registrada localmente (pelo descritor).');
        return { importado: true, via: 'descritor' };
      }
      motivo = motivo ?? 'Descritor do servidor em formato/versão desconhecidos';
    }

    return { importado: false, reason: motivo ?? 'Servidor sem foto nem descritor utilizáveis' };
  },

  migrarSePreciso: async (): Promise<'nada' | 'migrado' | 'recadastrar'> => {
    const motor = await resolverMotor();
    const guardado = await lerArmazenadoBruto();

    if (!guardado) return 'nada';                       // nunca cadastrou
    if (guardado.v === VERSAO[motor]) return 'nada';    // já está no espaço certo

    const nome = guardado.imageUri;
    const dir = (FileSystem as any).documentDirectory ?? '';
    if (!nome || !dir) {
      console.warn('[FaceRecognition] Template v' + guardado.v + ' sem foto guardada — recadastro necessário.');
      return 'recadastrar';
    }

    const uri = `${dir}${nome}`;
    try {
      const info = await FileSystem.getInfoAsync(uri);
      if (!info.exists) {
        console.warn('[FaceRecognition] Foto do cadastro sumiu do disco — recadastro necessário.');
        return 'recadastrar';
      }

      const { embedding } = await FaceRecognition.analisar(uri, { estrito: false });
      await FaceRecognition.saveReference([embedding], guardado.sourceHash, nome);

      console.log(
        `[FaceRecognition] Template migrado de v${guardado.v} para v${VERSAO[motor]} ` +
          `reprocessando a foto do cadastro.`,
      );
      return 'migrado';
    } catch (err: any) {
      console.warn('[FaceRecognition] Falha ao migrar o template:', err?.message ?? err);
      return 'recadastrar';
    }
  },

  enrollFromUris: async (
    imageUris: string[],
    opts?: {
      sourceHash?: string;
      /**
       * Roda DEPOIS do portao de qualidade e ANTES de gravar. Devolver uma string
       * recusa o cadastro com aquele motivo, sem persistir nada.
       *
       * Existe para a prova de vida. Ela precisa julgar um frame que ja passou pela
       * nitidez (senao acusa fraude de foto borrada - ver `FaceBiometria.cadastrar`),
       * e precisa julgar ANTES do `saveReference`, senao um ataque recusado ficaria
       * gravado no aparelho. Os candidatos vem do MAIS NITIDO para o menos.
       */
      antesDeGravar?: (
        candidatos: { uri: string; face: DetectedFace; nitidez: number }[],
      ) => Promise<string | null>;
      /**
       * Rosto ja detectado da PRIMEIRA foto — e so dela, porque e dessa que ele saiu.
       * Passar o rosto errado aqui recortaria o tile de outra imagem, entao as demais
       * seguem detectando normalmente.
       */
      face?: DetectedFace;
    },
  ): Promise<{ enrolled: boolean; reason?: string; aceitas: number }> => {
    const embeddings: Float32Array[] = [];
    const aceitas: { uri: string; face: DetectedFace; nitidez: number }[] = [];
    let primeiroMotivo: string | null = null;

    for (let i = 0; i < imageUris.length; i++) {
      const uri = imageUris[i];
      try {
        const { embedding, face, qualidade } = await FaceRecognition.analisar(uri, {
          estrito: true,
          face: i === 0 ? opts?.face : undefined,
        });
        embeddings.push(embedding);
        aceitas.push({ uri, face, nitidez: qualidade.nitidez });
      } catch (err: any) {
        if (!primeiroMotivo) primeiroMotivo = err?.message ?? 'Falha ao processar a foto';
      }
    }

    // Do mais nitido para o menos: decide quem a prova de vida julga e qual foto
    // vira a referencia gravada (e sobe para o banco, de onde o TirvuTab a le).
    aceitas.sort((a, b) => b.nitidez - a.nitidez);
    const melhorUri: string | null = aceitas[0]?.uri ?? null;

    if (embeddings.length === 0) {
      return {
        enrolled: false,
        reason: primeiroMotivo ?? 'Nenhuma foto com qualidade suficiente',
        aceitas: 0,
      };
    }

    // ── É um rosto, e é o mesmo rosto? ─────────────────────────────────────────
    // Consistência: os frames saem a centenas de ms um do outro, com a pessoa parada.
    // Embeddings do MESMO rosto ficam bem acima de 0,45 entre si; um recorte de
    // textura oscila com qualquer tremor da mão. Só julga com 2+ frames aceitos.
    if (embeddings.length >= 2) {
      let minimo = 1;
      for (let i = 0; i < embeddings.length; i++)
        for (let j = i + 1; j < embeddings.length; j++)
          minimo = Math.min(minimo, cosineSimilarity(embeddings[i], embeddings[j]));
      if (minimo < CONSISTENCIA_MIN_CADASTRO) {
        console.warn(`[FaceRecognition] cadastro recusado: frames inconsistentes (mín ${minimo.toFixed(3)})`);
        return {
          enrolled: false,
          reason: 'As fotos não parecem ser do mesmo rosto. Fique parado, olhando para a câmera, e tente de novo.',
          aceitas: 0,
        };
      }
    }
    // (Sem comparação com a referência anterior, de propósito: pode não haver nenhuma,
    // e em testes a face da conta é trocada de propósito — decisão de 12/09/2026.)

    if (opts?.antesDeGravar) {
      const recusa = await opts.antesDeGravar(aceitas);
      if (recusa) return { enrolled: false, reason: recusa, aceitas: 0 };
    }

    const savedImageUri = melhorUri
      ? await FaceRecognition.persistReferenceImage(melhorUri)
      : null;
    await FaceRecognition.saveReference(
      embeddings,
      opts?.sourceHash ?? `camera_${Date.now()}`,
      savedImageUri ?? undefined,
    );

    return { enrolled: true, aceitas: embeddings.length };
  },

  /** Atalho de uma foto só. Mantido pela compatibilidade com as telas. */
  enrollFromUri: async (
    imageUri: string,
  ): Promise<{ enrolled: boolean; reason?: string }> => {
    const res = await FaceRecognition.enrollFromUris([imageUri]);
    return { enrolled: res.enrolled, reason: res.reason };
  },

  /**
   * Cadastra a partir de uma foto base64 (o backend manda a foto do RH).
   * Usa hash da foto pra evitar reprocessar quando vier a mesma de novo.
   */
  enrollFromBase64: async (
    base64: string,
  ): Promise<{ enrolled: boolean; skipped: boolean; reason?: string }> => {
    if (!base64 || base64.length < 100) {
      return { enrolled: false, skipped: false, reason: 'Foto vazia ou inválida' };
    }

    const sourceHash = hashBase64(base64);
    if ((await FaceRecognition.loadReferenceHash()) === sourceHash) {
      return { enrolled: false, skipped: true };
    }

    const tempUri = `${(FileSystem as any).cacheDirectory}face_enroll_${Date.now()}.jpg`;
    try {
      await FileSystem.writeAsStringAsync(tempUri, base64, {
        encoding: FileSystem.EncodingType.Base64,
      });
      const info = await FileSystem.getInfoAsync(tempUri);
      if (!info.exists) {
        return { enrolled: false, skipped: false, reason: 'Falha ao salvar foto temporária' };
      }

      const res = await FaceRecognition.enrollFromUris([tempUri], { sourceHash });
      return { enrolled: res.enrolled, skipped: false, reason: res.reason };
    } catch (err: any) {
      return { enrolled: false, skipped: false, reason: err?.message || 'Falha ao gerar embedding' };
    } finally {
      try {
        await FileSystem.deleteAsync(tempUri, { idempotent: true });
      } catch {
        /* ignora — cacheDirectory é limpo pelo SO */
      }
    }
  },

  /**
   * Verifica uma foto contra os templates salvos.
   * Compara contra TODOS e fica com o melhor — é assim que o cadastro multi-foto paga.
   */
  verifyAgainstReference: async (
    imageUri: string,
    opts?: { face?: DetectedFace },
  ): Promise<ResultadoVerificacao> => {
    const referencias = await FaceRecognition.loadReference();
    if (!referencias) {
      return {
        matched: false,
        similarity: 0,
        hasReference: false,
        duvidoso: false,
        qualidade: null,
      };
    }

    const { embedding, qualidade } = await FaceRecognition.analisar(imageUri, {
      face: opts?.face,
      estrito: false,
    });

    let similarity = -1;
    for (const ref of referencias) {
      const s = cosineSimilarity(ref, embedding);
      if (s > similarity) similarity = s;
    }

    return {
      matched: similarity >= SIMILARITY_THRESHOLD,
      similarity,
      hasReference: true,
      duvidoso: similarity < SIMILARITY_THRESHOLD && similarity >= GRAY_ZONE,
      qualidade,
    };
  },

  CONFIG: {
    INPUT_SIZE: MODEL_INPUT_SIZE,
    // Dimensão e versão dependem do motor ativo, então saem como funções: uma
    // constante aqui congelaria o valor do mobilefacenet e mentiria no ArcFace.
    dimensao: async () => DIMENSAO[await resolverMotor()],
    versao: async () => VERSAO[await resolverMotor()],
    motor: async () => resolverMotor(),
    THRESHOLD: SIMILARITY_THRESHOLD,
    GRAY_ZONE,
    MAX_TEMPLATES,
    QUALIDADE,
  },
};
